import { useEffect, useState } from "react";
import { Camera, Image as ImageIcon, Send } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const BACKEND_ORIGIN = BACKEND_URL.endsWith("/api") ? BACKEND_URL.slice(0, -4) : BACKEND_URL;
const MAX_GALLERY_UPLOAD_BYTES = 15 * 1024 * 1024;
const FERIA_START_DAY = "2026-09-07";
const FERIA_END_DAY = "2026-09-17";

function formatDateValue(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatDayLabel(value) {
  const date = new Date(`${value}T12:00:00`);
  return date.toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "short",
  });
}

function resolvePhotoUrl(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${BACKEND_ORIGIN}${url}`;
}

export function resolveInitialGalleryDay(availableDays, currentDate = new Date()) {
  if (!Array.isArray(availableDays) || availableDays.length === 0) return "";

  const today = formatDateValue(currentDate);
  if (today >= FERIA_START_DAY && today <= FERIA_END_DAY && availableDays.includes(today)) {
    return today;
  }

  return availableDays[0];
}

export default function GaleriaVisitantes() {
  const [days, setDays] = useState([]);
  const [selectedDay, setSelectedDay] = useState("");
  const [photos, setPhotos] = useState([]);
  const [loadingDays, setLoadingDays] = useState(true);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [galleryError, setGalleryError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [form, setForm] = useState({
    date: "",
    file: null,
    comment: "",
    legalAcceptance: false,
  });

  async function fetchPhotosForDay(day, options = {}) {
    const { silentError = false } = options;
    if (!day) return;
    setLoadingPhotos(true);
    if (!silentError) setGalleryError("");
    try {
      const response = await api.get("/visitor-gallery", {
        params: { date: day },
      });
      setPhotos(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      if (!silentError) {
        setGalleryError("No se han podido cargar las fotos de este día.");
      }
      setPhotos([]);
    } finally {
      setLoadingPhotos(false);
    }
  }

  useEffect(() => {
    let alive = true;

    async function loadDays() {
      setLoadingDays(true);
      setGalleryError("");
      try {
        const response = await api.get("/visitor-gallery/days");
        const availableDays = Array.isArray(response.data) ? response.data : [];
        if (!alive) return;
        setDays(availableDays);
        if (availableDays.length > 0) {
          const initialDay = resolveInitialGalleryDay(availableDays);
          setSelectedDay((current) => current || initialDay);
          setForm((current) => ({
            ...current,
            date: current.date || initialDay,
          }));
        }
      } catch (error) {
        if (!alive) return;
        setGalleryError("No se han podido cargar los días de la galería.");
      } finally {
        if (alive) setLoadingDays(false);
      }
    }

    loadDays();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    fetchPhotosForDay(selectedDay);
  }, [selectedDay]);

  function updateForm(key, value) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSubmitError("");
    setSubmitSuccess("");

    const formElement = event.currentTarget;
    const rawFormData = new FormData(formElement);
    const date = String(rawFormData.get("date") || form.date || "");
    const file = rawFormData.get("file");
    const comment = String(rawFormData.get("comment") || "");
    const legalAccepted =
      rawFormData.get("legal_acceptance") === "true" ||
      rawFormData.get("legal_acceptance") === "on" ||
      form.legalAcceptance;

    if (!date) {
      setSubmitError("Selecciona el día de la foto.");
      return;
    }
    if (!(file instanceof File) || file.size === 0) {
      setSubmitError("Selecciona una imagen antes de enviar.");
      return;
    }
    if (!legalAccepted) {
      setSubmitError("Debes aceptar la casilla legal para continuar.");
      return;
    }
    if (file.size > MAX_GALLERY_UPLOAD_BYTES) {
      setSubmitError("La imagen supera el máximo de 15 MB.");
      return;
    }

    const data = new FormData();
    data.append("file", file);
    data.append("date", date);
    data.append("comment", comment);
    data.append("legal_acceptance", "true");

    setSubmitting(true);

    try {
      const response = await api.post("/visitor-gallery/upload", data);
      if (response.status !== 200 && response.status !== 201) {
        throw new Error(`Unexpected upload status: ${response.status}`);
      }
    } catch (error) {
      console.error("Visitor gallery upload failed", error);
      setSubmitError(formatApiError(error.response?.data?.detail));
      setSubmitting(false);
      return;
    }

    setSubmitSuccess("Tu foto se ha enviado correctamente y será revisada antes de publicarse.");

    const fileInput = formElement.querySelector('input[type="file"]');
    if (fileInput) fileInput.value = "";

    setForm((current) => ({
      ...current,
      date,
      file: null,
      comment: "",
      legalAcceptance: false,
    }));
    setSelectedDay(date);
    await fetchPhotosForDay(date, { silentError: true });
    setSubmitting(false);
  }

  return (
    <div className="space-y-8 animate-in fade-in duration-500" data-testid="galeria-page">
      <header className="py-4">
        <div className="text-xs uppercase tracking-[0.25em] text-primary font-bold">Galería de visitantes</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl mt-1">La Feria, vista por vosotros</h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">
          Elige un día para ver las fotos publicadas y comparte la tuya desde el móvil.
        </p>
      </header>

      <section className="bg-white rounded-3xl border border-border p-5 sm:p-6 space-y-5" data-testid="galeria-upload-section">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 shrink-0 rounded-full bg-primary text-primary-foreground grid place-items-center">
            <Camera size={20} />
          </div>
          <div>
            <h2 className="font-display font-black text-2xl">Sube tu foto</h2>
            <p className="text-sm text-muted-foreground mt-1">Tu foto será revisada antes de publicarse.</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="galeria-upload-form">
          <div>
            <label htmlFor="gallery-date" className="block text-sm font-bold mb-2">
              Día
            </label>
            <select
              id="gallery-date"
              name="date"
              value={form.date}
              onChange={(event) => updateForm("date", event.target.value)}
              className="w-full rounded-xl border border-border bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-ring"
              data-testid="galeria-form-date"
            >
              {days.map((day) => (
                <option key={day} value={day}>
                  {formatDayLabel(day)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="gallery-file" className="block text-sm font-bold mb-2">
              Foto
            </label>
            <input
              id="gallery-file"
              name="file"
              type="file"
              accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
              onChange={(event) => updateForm("file", event.target.files?.[0] || null)}
              className="w-full rounded-xl border border-border bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              data-testid="galeria-form-file"
            />
            <p className="text-xs text-muted-foreground mt-2">Formatos admitidos: JPG, JPEG, PNG y WEBP. Máximo 15 MB.</p>
          </div>

          <div>
            <label htmlFor="gallery-comment" className="block text-sm font-bold mb-2">
              Comentario opcional
            </label>
            <textarea
              id="gallery-comment"
              name="comment"
              rows={3}
              value={form.comment}
              onChange={(event) => updateForm("comment", event.target.value)}
              placeholder="Puedes añadir una nota breve sobre la foto..."
              className="w-full rounded-xl border border-border bg-white px-4 py-3 resize-none focus:outline-none focus:ring-2 focus:ring-ring"
              data-testid="galeria-form-comment"
            />
          </div>

          <label className="flex items-start gap-3 rounded-2xl border border-border p-4 bg-accent/30" data-testid="galeria-form-legal">
            <input
              name="legal_acceptance"
              type="checkbox"
              value="true"
              checked={form.legalAcceptance}
              onChange={(event) => updateForm("legalAcceptance", event.target.checked)}
              className="mt-1 h-4 w-4"
            />
            <span className="text-sm text-foreground/90">
              Confirmo que tengo derecho a compartir esta imagen y autorizo su revisión antes de la publicación.
            </span>
          </label>

          {submitError ? (
            <div
              className="rounded-2xl p-4 text-sm bg-destructive/10 text-destructive border border-destructive/20"
              data-testid="galeria-submit-error"
            >
              {submitError}
            </div>
          ) : null}

          {submitSuccess ? (
            <div
              className="rounded-2xl p-4 text-sm bg-primary/10 text-foreground border border-primary/20"
              data-testid="galeria-submit-success"
            >
              {submitSuccess}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={submitting || loadingDays || days.length === 0}
            className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-5 py-3.5 rounded-full font-bold hover:brightness-110 disabled:opacity-60 transition"
            data-testid="galeria-submit-btn"
          >
            <Send size={16} />
            {submitting ? "Enviando..." : "Enviar foto"}
          </button>
        </form>
      </section>

      <section className="space-y-4" data-testid="galeria-public-section">
        <div>
          <h2 className="font-display font-black text-2xl">Fotos publicadas</h2>
          <p className="text-sm text-muted-foreground mt-1">Solo se muestran imágenes aprobadas para el día seleccionado.</p>
        </div>

        {loadingDays ? (
          <div className="bg-white rounded-2xl border border-border p-6 text-muted-foreground">Cargando días...</div>
        ) : days.length === 0 ? (
          <div className="bg-white rounded-2xl border border-border p-6 text-muted-foreground">
            Aún no hay días disponibles para la galería.
          </div>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="galeria-day-tabs">
            {days.map((day) => {
              const active = selectedDay === day;
              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => setSelectedDay(day)}
                  className={`px-4 py-2 rounded-full border text-sm font-bold transition ${
                    active ? "bg-primary text-primary-foreground border-primary" : "border-border bg-white hover:bg-accent"
                  }`}
                  data-testid={`galeria-day-${day}`}
                >
                  {formatDayLabel(day)}
                </button>
              );
            })}
          </div>
        )}

        {galleryError ? (
          <div className="bg-white rounded-2xl border border-destructive/30 p-4 text-sm text-destructive" data-testid="galeria-error">
            {galleryError}
          </div>
        ) : null}

        {loadingPhotos ? (
          <div className="bg-white rounded-2xl border border-border p-8 text-muted-foreground">Cargando fotos...</div>
        ) : photos.length === 0 ? (
          <div className="bg-white rounded-2xl border border-border p-8 text-center" data-testid="galeria-empty">
            <ImageIcon size={30} className="mx-auto text-muted-foreground" />
            <p className="mt-3 text-muted-foreground">Todavía no hay fotos publicadas para este día.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4" data-testid="galeria-grid">
            {photos.map((photo) => (
              <article
                key={photo.id}
                className="bg-white rounded-2xl border border-border overflow-hidden"
                data-testid={`galeria-photo-${photo.id}`}
              >
                <img
                  src={resolvePhotoUrl(photo.url)}
                  alt={photo.comment?.trim() ? photo.comment : `Foto del ${formatDayLabel(photo.date)}`}
                  className="w-full aspect-square object-cover"
                  loading="lazy"
                />
                {photo.comment?.trim() ? (
                  <div className="p-3 text-sm text-muted-foreground">{photo.comment}</div>
                ) : null}
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
